import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import type {
  CapabilityState,
  CloudflareAccessUser,
  CloudflareIdentityProvider,
  CloudflareScimConnection,
  ScimSyncOperation,
  ScimSyncPlan,
  ScimSyncSelection,
} from "../shared/types";
import { useAdminApi, useRefreshCloudflare } from "./admin-query";
import { CopyButton } from "./AdminUi";
import { errorMessage } from "./api";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { PaginationControls, usePagination } from "./pagination";
import type { CloudflareOverview } from "./types";

function operationKey(
  operation: Pick<ScimSyncOperation, "resourceType" | "externalId">,
): string {
  return `${operation.resourceType}:${operation.externalId}`;
}

function isActionable(
  operation: ScimSyncOperation,
): operation is ScimSyncOperation & ScimSyncSelection {
  return operation.action === "create" || operation.action === "update";
}

type ReconciliationTone = "positive" | "warning" | "neutral";

interface ReconciliationStatus {
  label: string;
  detail: string;
  tone: ReconciliationTone;
}

export function scimReconciliationStatus(
  action: ScimSyncOperation["action"],
): ReconciliationStatus {
  if (action === "unchanged") {
    return {
      label: "SCIM in sync",
      detail: "The SCIM resource matches the local directory.",
      tone: "positive",
    };
  }
  if (action === "update") {
    return {
      label: "SCIM out of sync",
      detail: "The SCIM resource differs from the local directory.",
      tone: "warning",
    };
  }
  return {
    label: "Not SCIM managed",
    detail:
      "This owned resource has not been created through this SCIM connection.",
    tone: "warning",
  };
}

export function registryReconciliationStatus(
  operation: ScimSyncOperation,
  registryByEmail: ReadonlyMap<string, CloudflareAccessUser>,
  capability: CapabilityState,
): ReconciliationStatus | undefined {
  if (operation.resourceType !== "user") return undefined;
  if (capability !== "available") {
    return {
      label: "Registry unavailable",
      detail: "Cloudflare User Registry data could not be loaded.",
      tone: "neutral",
    };
  }

  const registryUser = operation.email
    ? registryByEmail.get(operation.email.trim().toLowerCase())
    : undefined;
  if (!registryUser) {
    return {
      label: "Awaiting first login",
      detail:
        "No matching Cloudflare User Registry identity was found. It normally appears after the first Access or Cloudflare One Client login.",
      tone: "neutral",
    };
  }

  const activeInCloudflare =
    registryUser.accessSeat ||
    registryUser.gatewaySeat ||
    registryUser.sessions.length > 0;
  if (operation.localStatus === "disabled" && activeInCloudflare) {
    return {
      label: "Deprovision required",
      detail:
        "The local user is disabled but still has a Cloudflare seat or active session.",
      tone: "warning",
    };
  }
  return activeInCloudflare
    ? {
        label: "Registry active",
        detail: "Matched to an active Cloudflare identity by email.",
        tone: "positive",
      }
    : {
        label: "Registry inactive",
        detail:
          "Matched by email, but the Cloudflare identity has no active seat or session.",
        tone: "neutral",
      };
}

export function toggleScimOperationSelection(
  operations: ScimSyncOperation[],
  current: ReadonlySet<string>,
  operation: ScimSyncOperation,
  checked: boolean,
): Set<string> {
  if (!isActionable(operation)) return new Set(current);
  const next = new Set(current);
  const key = operationKey(operation);
  if (checked) {
    next.add(key);
    for (const dependency of operation.dependsOn ?? []) {
      next.add(`user:${dependency}`);
    }
  } else {
    next.delete(key);
    if (operation.resourceType === "user") {
      for (const candidate of operations) {
        if (candidate.dependsOn?.includes(operation.externalId)) {
          next.delete(operationKey(candidate));
        }
      }
    }
  }
  return next;
}

export function CloudflareProvisioningSection({
  automation,
  identityProviders,
  registryCapability,
  registryUsers,
  scimConnections,
}: {
  automation: CloudflareOverview["automation"];
  identityProviders: CloudflareIdentityProvider[];
  registryCapability: CapabilityState;
  registryUsers: CloudflareAccessUser[];
  scimConnections: CloudflareScimConnection[];
}) {
  const adminApi = useAdminApi();
  const refresh = useRefreshCloudflare();
  const [confirming, setConfirming] = useState(false);
  const [selectedOperationKeys, setSelectedOperationKeys] = useState<
    Set<string>
  >(new Set());
  const [pendingOperations, setPendingOperations] = useState<
    ScimSyncSelection[]
  >([]);
  const [selectedProviderId, setSelectedProviderId] = useState(
    () =>
      identityProviders.find(
        (provider) =>
          provider.scim.enabled &&
          scimConnections.some(
            (connection) => connection.identityProviderId === provider.id,
          ),
      )?.id ??
      identityProviders[0]?.id ??
      "",
  );
  const selectedProvider =
    identityProviders.find((provider) => provider.id === selectedProviderId) ??
    identityProviders[0];
  const selectedConnection = scimConnections.find(
    (connection) => connection.identityProviderId === selectedProvider?.id,
  );
  const scimReady = Boolean(
    selectedProvider?.scim.enabled && selectedConnection,
  );
  const scimState = !selectedProvider
    ? "not configured"
    : !selectedProvider.scim.enabled
      ? "disabled"
      : !selectedConnection
        ? "credential required"
        : "ready";
  const scimPath = selectedProvider
    ? `/api/admin/cloudflare/identity-providers/${encodeURIComponent(selectedProvider.id)}/scim`
    : "";

  const previewMutation = useMutation({
    mutationFn: () => adminApi<{ plan: ScimSyncPlan }>(`${scimPath}/plan`),
  });
  const plan = previewMutation.data?.plan;
  const actionableOperations = useMemo(
    () => plan?.operations.filter(isActionable) ?? [],
    [plan],
  );
  const registryByEmail = useMemo(
    () =>
      new Map(
        registryUsers.map((user) => [user.email.trim().toLowerCase(), user]),
      ),
    [registryUsers],
  );

  useEffect(() => {
    setSelectedOperationKeys(new Set(actionableOperations.map(operationKey)));
    setPendingOperations([]);
    setConfirming(false);
  }, [actionableOperations]);

  const selectedOperations = actionableOperations
    .filter((operation) => selectedOperationKeys.has(operationKey(operation)))
    .map(({ resourceType, externalId, action }) => ({
      resourceType,
      externalId,
      action,
    }));
  const selectedSummary = selectedOperations.reduce(
    (summary, operation) => {
      summary[operation.action] += 1;
      return summary;
    },
    { create: 0, update: 0 },
  );

  function toggleOperation(operation: ScimSyncOperation, checked: boolean) {
    setSelectedOperationKeys((current) =>
      toggleScimOperationSelection(
        actionableOperations,
        current,
        operation,
        checked,
      ),
    );
  }

  const applyMutation = useMutation({
    mutationFn: (selection: ScimSyncSelection[]) => {
      const summary = selection.reduce(
        (counts, operation) => {
          counts[operation.action] += 1;
          return counts;
        },
        { create: 0, update: 0 },
      );
      return adminApi(`${scimPath}/apply`, {
        method: "POST",
        body: JSON.stringify({
          confirmation: "SYNC DIRECTORY",
          expectedCreate: summary.create,
          expectedUpdate: summary.update,
          selectedOperations: selection,
        }),
      });
    },
    // A failure keeps the confirmation open so it can report the reason.
    onSuccess: async () => {
      setConfirming(false);
      previewMutation.mutate();
      await refresh();
    },
  });

  // Each preview returns a fresh plan, so its identity resets the page.
  const [operationSlice, setOperationPage] = usePagination(
    plan?.operations ?? [],
    20,
    plan,
  );
  const loading = previewMutation.isPending || applyMutation.isPending;
  const error =
    (previewMutation.error &&
      errorMessage(
        previewMutation.error,
        "The SCIM plan could not be loaded.",
      )) ||
    (applyMutation.error &&
      errorMessage(
        applyMutation.error,
        "The SCIM plan could not be applied.",
      )) ||
    "";

  return (
    <section className="panel-card cloudflare-integration-section">
      <header className="panel-card__header">
        <div>
          <span className="eyebrow">Identity automation</span>
          <h2>Provisioning and adaptive checks</h2>
        </div>
      </header>
      <div className="integration-card-grid">
        <article className="integration-card">
          <div className="integration-card__heading">
            <strong>Turnstile</strong>
            <span
              className={`panel-status panel-status--${automation.turnstile.state === "ready" ? "positive" : "warning"}`}
            >
              {automation.turnstile.state.replaceAll("_", " ")}
            </span>
          </div>
          <p>
            Bot challenge on the local credential login, verified server-side.
          </p>
          {automation.turnstile.expectedHostname && (
            <small>Hostname: {automation.turnstile.expectedHostname}</small>
          )}
          {automation.turnstile.missingBindings.length > 0 && (
            <small>
              Missing: {automation.turnstile.missingBindings.join(", ")}
            </small>
          )}
        </article>

        <article className="integration-card">
          <div className="integration-card__heading">
            <strong>External Evaluation</strong>
            <span
              className={`panel-status panel-status--${automation.externalEvaluation.state === "ready" ? "positive" : "warning"}`}
            >
              {automation.externalEvaluation.state.replaceAll("_", " ")}
            </span>
          </div>
          <p>
            Real-time allow/deny from the current directory status and groups.
          </p>
          <div className="integration-endpoint">
            <code>{automation.externalEvaluation.evaluateUrl}</code>
            <CopyButton
              label="Copy Evaluate URL"
              value={automation.externalEvaluation.evaluateUrl}
            />
          </div>
          <div className="integration-endpoint">
            <code>{automation.externalEvaluation.keysUrl}</code>
            <CopyButton
              label="Copy Keys URL"
              value={automation.externalEvaluation.keysUrl}
            />
          </div>
        </article>

        <article className="integration-card integration-card--scim">
          <div className="integration-card__heading">
            <strong>SCIM directory sync</strong>
            <span
              className={`panel-status panel-status--${scimReady ? "positive" : "warning"}`}
            >
              {scimState}
            </span>
          </div>
          <p>
            Preview owned user and group upserts before sending them to
            Cloudflare.
          </p>
          <label className="select-field scim-provider-select">
            <span>Identity provider</span>
            <select
              disabled={loading || identityProviders.length === 0}
              onChange={(event) => {
                setSelectedProviderId(event.target.value);
                setConfirming(false);
                previewMutation.reset();
                applyMutation.reset();
              }}
              value={selectedProvider?.id ?? ""}
            >
              {identityProviders.length === 0 && (
                <option value="">No identity providers</option>
              )}
              {identityProviders.map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.name}
                </option>
              ))}
            </select>
          </label>
          {selectedProvider && !selectedProvider.scim.enabled && (
            <small>Enable SCIM for this provider before synchronizing.</small>
          )}
          {selectedProvider?.scim.enabled && !selectedConnection && (
            <small>Store this provider's SCIM credential first.</small>
          )}
          <button
            className="button button--secondary button--compact"
            disabled={loading || !scimReady}
            onClick={() => previewMutation.mutate()}
            type="button"
          >
            {loading ? "Loading…" : "Preview sync"}
          </button>
        </article>
      </div>

      {error && <div className="notice notice--error">{error}</div>}
      {plan && (
        <div className="scim-plan">
          <div className="scim-plan__summary">
            <span>
              <strong>{plan.summary.create}</strong> not managed
            </span>
            <span>
              <strong>{plan.summary.update}</strong> out of sync
            </span>
            <span>
              <strong>{plan.summary.unchanged}</strong> in sync
            </span>
            <span className="scim-plan__selected-count">
              <strong>{selectedOperations.length}</strong> selected
            </span>
            <button
              className="button button--secondary button--compact"
              disabled={
                loading ||
                selectedOperations.length === actionableOperations.length
              }
              onClick={() =>
                setSelectedOperationKeys(
                  new Set(actionableOperations.map(operationKey)),
                )
              }
              type="button"
            >
              Select all changes
            </button>
            <button
              className="button button--secondary button--compact"
              disabled={loading || selectedOperations.length === 0}
              onClick={() => setSelectedOperationKeys(new Set())}
              type="button"
            >
              Clear
            </button>
            <button
              className="button button--primary button--compact"
              disabled={loading || selectedOperations.length === 0}
              onClick={() => {
                applyMutation.reset();
                setPendingOperations(selectedOperations);
                setConfirming(true);
              }}
              type="button"
            >
              Apply {selectedOperations.length} selected
            </button>
          </div>
          <p className="scim-plan__explanation">
            Users are matched to the account-wide Cloudflare registry by email.
            SCIM users and groups are matched by CODEMONDAY-owned external ID.
            Identities outside this application's scope are excluded.
          </p>
          <ul className="scim-operation-list">
            {operationSlice.items.map((operation) => {
              const scimStatus = scimReconciliationStatus(operation.action);
              const registryStatus = registryReconciliationStatus(
                operation,
                registryByEmail,
                registryCapability,
              );
              return (
                <li key={`${operation.resourceType}:${operation.externalId}`}>
                  <label className="scim-operation-list__selection">
                    <input
                      aria-label={`Select ${operation.displayName}`}
                      checked={
                        isActionable(operation) &&
                        selectedOperationKeys.has(operationKey(operation))
                      }
                      disabled={loading || !isActionable(operation)}
                      onChange={(event) =>
                        toggleOperation(operation, event.target.checked)
                      }
                      type="checkbox"
                    />
                    <span>
                      <strong>{operation.displayName}</strong>
                      <small>
                        {operation.resourceType}
                        {operation.email ? ` · ${operation.email}` : ""}
                        {operation.dependsOn?.length
                          ? ` · includes ${operation.dependsOn.length} required ${operation.dependsOn.length === 1 ? "user" : "users"}`
                          : ""}
                      </small>
                    </span>
                  </label>
                  <div className="scim-operation-list__statuses">
                    {registryStatus && (
                      <span
                        aria-label={`${registryStatus.label}. ${registryStatus.detail}`}
                        className={`panel-status panel-status--${registryStatus.tone}`}
                        title={registryStatus.detail}
                      >
                        {registryStatus.label}
                      </span>
                    )}
                    <span
                      aria-label={`${scimStatus.label}. ${scimStatus.detail}`}
                      className={`panel-status panel-status--${scimStatus.tone}`}
                      title={scimStatus.detail}
                    >
                      {scimStatus.label}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
          <PaginationControls
            label="operations"
            onPageChange={setOperationPage}
            slice={operationSlice}
          />
        </div>
      )}

      {confirming && plan && (
        <ConfirmationDialog
          busy={loading}
          busyLabel="Synchronizing…"
          confirmLabel="Synchronize directory"
          description={`Apply ${selectedSummary.create} creates and ${selectedSummary.update} updates. Resources not owned by CODEMONDAY Identity will not be changed or deleted.`}
          destructive={false}
          error={
            applyMutation.error
              ? errorMessage(
                  applyMutation.error,
                  "The SCIM plan could not be applied.",
                )
              : ""
          }
          icon="users"
          onCancel={() => !loading && setConfirming(false)}
          onConfirm={() => applyMutation.mutate(pendingOperations)}
          title="Apply this SCIM plan?"
        />
      )}
    </section>
  );
}
